/**
 * WP-9 audio — the PURE synth layer. Every cue is rendered to a plain
 * Float32Array of PCM (no WebAudio, no timers, no DOM), so it can be unit-tested
 * offline: assert non-silent, no clipping (peak < 1), expected duration, and
 * byte-for-byte determinism. The engine (audio/engine.ts) wraps these buffers in
 * AudioBuffers for playback. Synth-generated → no encumbered .wav assets.
 *
 * Sound design ported from legacy/admirals-return.html (procedural WebAudio SFX +
 * a generative 6/8 score), re-expressed as fixed, deterministic offline renders.
 * Randomness comes only from the seeded PRNG (rules/rng.ts).
 */
import { makeRng, type Rng } from '../rules/rng';
import type { ShipType } from '../rules/state';

export const SR = 22050; // plenty for retro SFX; matches the legacy assets

export interface Pcm {
  sampleRate: number;
  data: Float32Array;
}

const HEAVY = new Set<ShipType>(['CV', 'BB', 'CR']);

function mono(seconds: number): Float32Array {
  return new Float32Array(Math.max(1, Math.round(SR * seconds)));
}

/** Scale down so the absolute peak never reaches 1.0 (guaranteed no clipping). */
function clampPeak(d: Float32Array, ceil = 0.95): Float32Array {
  let peak = 0;
  for (let i = 0; i < d.length; i++) peak = Math.max(peak, Math.abs(d[i]!));
  if (peak > ceil && peak > 0) {
    const k = ceil / peak;
    for (let i = 0; i < d.length; i++) d[i]! *= k;
  }
  return d;
}

/** One-pole lowpass in place: higher `a` (0..1) = brighter. */
function lowpass(d: Float32Array, a: number): void {
  let y = 0;
  for (let i = 0; i < d.length; i++) {
    y += a * (d[i]! - y);
    d[i] = y;
  }
}

function fillNoise(d: Float32Array, rng: Rng, shape: (t: number) => number): void {
  const n = d.length;
  for (let i = 0; i < n; i++) d[i] = (rng.next() * 2 - 1) * shape(i / n);
}

/** Per-class gun report. Heavy ships boom; light ships crack; subs run torpedoes. */
export function fireFor(type: ShipType): Pcm {
  if (type === 'SS') return torpedo();
  const heavy = HEAVY.has(type);
  const dur = heavy ? 0.46 : 0.22;
  const d = mono(dur);
  const rng = makeRng(heavy ? 0x6a17 : 0x10b3);
  fillNoise(d, rng, (t) => Math.pow(1 - t, heavy ? 1.8 : 2.6));
  lowpass(d, heavy ? 0.06 : 0.22);
  // low thump under heavy guns
  if (heavy) {
    const n = d.length;
    for (let i = 0; i < n; i++) {
      const t = i / SR;
      d[i]! += Math.sin(2 * Math.PI * 68 * t) * 0.5 * Math.pow(1 - i / n, 2.4);
    }
  }
  for (let i = 0; i < d.length; i++) d[i]! *= heavy ? 0.85 : 0.6;
  return { sampleRate: SR, data: clampPeak(d) };
}

/** Submarine torpedo: a rising filtered-noise whoosh with a soft bubble wobble. */
export function torpedo(): Pcm {
  const d = mono(0.6);
  const rng = makeRng(0x7c0d);
  const n = d.length;
  for (let i = 0; i < n; i++) {
    const t = i / n;
    const bubble = 0.6 + 0.4 * Math.sin(2 * Math.PI * (9 + 5 * t) * (i / SR));
    d[i] = (rng.next() * 2 - 1) * Math.sin(Math.PI * t) * bubble * 0.6;
  }
  lowpass(d, 0.14);
  return { sampleRate: SR, data: clampPeak(d) };
}

/** A meaty explosion: low-passed noise blast + a descending sine boom. */
export function explosion(): Pcm {
  const d = mono(0.8);
  const rng = makeRng(0x4f21);
  const n = d.length;
  fillNoise(d, rng, (t) => Math.pow(1 - t, 1.5));
  lowpass(d, 0.09);
  for (let i = 0; i < n; i++) {
    const t = i / SR;
    const f = 58 - 18 * (i / n);
    d[i]! += Math.sin(2 * Math.PI * f * t) * 0.55 * Math.pow(1 - i / n, 1.4);
  }
  for (let i = 0; i < n; i++) d[i]! *= 0.9;
  return { sampleRate: SR, data: clampPeak(d) };
}

/** A ship going under: descending sawtooth + a gurgly noise tail (legacy SFX.sink). */
export function sink(): Pcm {
  const d = mono(0.85);
  const rng = makeRng(0x2b88);
  const n = d.length;
  let phase = 0;
  for (let i = 0; i < n; i++) {
    const prog = i / n;
    const f = 200 * Math.pow(45 / 200, prog); // 200 → 45 Hz exp glide
    phase += f / SR;
    const saw = 2 * (phase - Math.floor(phase)) - 1;
    const env = Math.pow(1 - prog, 1.2);
    const gurgle = (rng.next() * 2 - 1) * 0.25 * env * Math.max(0, prog - 0.3);
    d[i] = saw * 0.32 * env + gurgle;
  }
  lowpass(d, 0.5);
  return { sampleRate: SR, data: clampPeak(d) };
}

/** A short UI blip. */
export function click(): Pcm {
  const d = mono(0.05);
  const n = d.length;
  for (let i = 0; i < n; i++) {
    const t = i / SR;
    d[i] = Math.sin(2 * Math.PI * 1300 * t) * 0.3 * Math.pow(1 - i / n, 3);
  }
  return { sampleRate: SR, data: clampPeak(d) };
}

/** A light water splash (shell near-miss). */
export function splash(): Pcm {
  const d = mono(0.35);
  const rng = makeRng(0x55aa);
  fillNoise(d, rng, (t) => Math.pow(1 - t, 1.6) * (0.4 + 0.6 * Math.min(1, t * 6)));
  lowpass(d, 0.4);
  for (let i = 0; i < d.length; i++) d[i]! *= 0.5;
  return { sampleRate: SR, data: clampPeak(d) };
}

/** Victory fanfare: an ascending major arpeggio resolving to a held triad. */
export function fanfare(): Pcm {
  const d = mono(1.4);
  const note = (freq: number, start: number, len: number, vol: number): void => {
    const s = Math.round(start * SR);
    const e = Math.min(d.length, s + Math.round(len * SR));
    for (let i = s; i < e; i++) {
      const tt = (i - s) / SR;
      const k = (i - s) / (e - s);
      const env = Math.min(1, k * 8) * Math.pow(1 - k, 1.1); // soft attack, gentle release
      // triangle-ish: sine + a touch of third harmonic
      const v = Math.sin(2 * Math.PI * freq * tt) + 0.18 * Math.sin(2 * Math.PI * 3 * freq * tt);
      d[i]! += v * env * vol;
    }
  };
  const C5 = 523.25;
  const E5 = 659.25;
  const G5 = 783.99;
  const C6 = 1046.5;
  note(C5, 0.0, 0.18, 0.16);
  note(E5, 0.16, 0.18, 0.16);
  note(G5, 0.32, 0.18, 0.16);
  note(C6, 0.48, 0.5, 0.18);
  // held chord
  note(C5, 0.6, 0.8, 0.1);
  note(E5, 0.6, 0.8, 0.1);
  note(G5, 0.6, 0.8, 0.1);
  return { sampleRate: SR, data: clampPeak(d) };
}

// chord progression as semitone offsets from A2 (root/third/fifth): Am F G C Am F G E
const PROG = [
  [0, 3, 7], [-4, 0, 3], [-2, 2, 5], [3, 7, 10],
  [0, 3, 7], [-4, 0, 3], [-2, 2, 5], [-5, -2, 2],
];
const semi = (s: number, oct = 0): number => 110 * Math.pow(2, s / 12 + oct);

/**
 * A loopable score bed: the full 8-bar 6/8 progression rendered once (≈14 s).
 * Deterministic (seeded improv), so the engine just loops the AudioBuffer.
 */
export function music(): Pcm {
  const EIGHTH = 0.3;
  const bars = PROG.length;
  const total = bars * 6 * EIGHTH;
  const d = mono(total);
  const rng = makeRng(0x1234abcd);
  const tail = 0.35; // stop scheduling near the end so envelopes finish in-buffer
  const note = (freq: number, start: number, len: number, vol: number, kind: 'sine' | 'tri'): void => {
    const s = Math.round(start * SR);
    const dur = Math.min(len, total - tail - start);
    if (dur <= 0) return;
    const e = Math.min(d.length, s + Math.round(dur * SR));
    for (let i = s; i < e; i++) {
      const tt = (i - s) / SR;
      const k = (i - s) / (e - s);
      const env = Math.min(1, k / 0.15) * (k > 0.55 ? Math.max(0, 1 - (k - 0.55) / 0.45) : 1);
      const ph = freq * tt;
      const wave = kind === 'tri'
        ? 2 * Math.abs(2 * (ph - Math.floor(ph + 0.5))) - 1 // triangle
        : Math.sin(2 * Math.PI * ph);
      d[i]! += wave * env * vol;
    }
  };
  let lastDeg = 0;
  for (let bar = 0; bar < bars; bar++) {
    const chord = PROG[bar % PROG.length]!;
    for (let beat = 0; beat < 6; beat++) {
      const t = (bar * 6 + beat) * EIGHTH;
      if (beat === 0) {
        note(semi(chord[0]!, 0), t, EIGHTH * 5.6, 0.16, 'tri'); // bass root
        note(semi(chord[2]!, 2), t, EIGHTH * 5.8, 0.06, 'sine'); // pad fifth
      }
      if (beat === 3 && rng.next() < 0.8) {
        note(semi(chord[0]!, 1), t, EIGHTH * 2.6, 0.09, 'tri');
      }
      const density = bar % 8 < 4 ? 0.55 : 0.75;
      if (rng.next() < density && beat % 2 === 0) {
        const roll = rng.next();
        let deg: number;
        if (roll < 0.55) deg = chord[rng.int(3)]!;
        else if (roll < 0.8) deg = lastDeg + (rng.next() < 0.5 ? 2 : -2);
        else deg = chord[0]! + 12;
        lastDeg = deg;
        const len = rng.next() < 0.3 ? EIGHTH * 3.8 : EIGHTH * 1.9;
        note(semi(deg, 2), t, len, 0.11, 'sine');
      }
    }
  }
  return { sampleRate: SR, data: clampPeak(d, 0.85) };
}
